/**
 * The forge's art (ADR-037 D5, D6): GET /v1/forge/forges/:id/art — the
 * owner's approval threshold, the latest render of every asset in
 * art/assets.json, recent batches — and setting the threshold. Re-read every
 * few seconds while a turn runs (Claude may be making art), once otherwise.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { forgeQueryKeys } from '../api/queryKeys';
import type { ForgeArtOverview } from '../types';

/** How often the art is re-read while a turn runs. */
export const FORGE_ART_POLL_MS = 5000;

export interface UseForgeArtResult {
  art: ForgeArtOverview | null;
  isLoading: boolean;
  /** Set the threshold in credits, or null to be asked about every batch. */
  setThreshold: (credits: number | null) => Promise<number | null>;
  isSavingThreshold: boolean;
}

export function useForgeArt(
  forgeId: string | null,
  options: { turnRunning?: boolean } = {},
): UseForgeArtResult {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: forgeQueryKeys.art(forgeId ?? ''),
    queryFn: () => api.getForgeArt(forgeId!),
    enabled: !!forgeId,
    refetchInterval: options.turnRunning ? FORGE_ART_POLL_MS : false,
  });
  const mutation = useMutation({
    mutationFn: (credits: number | null) => api.setForgeArtThreshold(forgeId!, credits),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: forgeQueryKeys.art(forgeId ?? '') }),
  });
  return {
    art: query.data ?? null,
    isLoading: query.isLoading,
    setThreshold: async (credits) => (await mutation.mutateAsync(credits)).approvalThresholdCredits,
    isSavingThreshold: mutation.isPending,
  };
}
